import { TypeProgramsPageFields } from "contentful-generated";
import ArrowedButtonLink from "components/ArrowedButtonLink";
import ScrollableImage from "components/ScrollableImage";
import BicoloredTitle from "components/BicoloredTitle";
import { getProgramsPage } from "repos/programsPage";
import { getContentfulImageUrl } from "utils/utils";
import GridLayout from "components/GridLayout";
import { GetStaticPropsContext } from "next";
import Section from "components/Section";
import Banner from "components/Banner";
import Header from "components/Header";
import Layout from "components/Layout";
import Title from "components/Title";
import Image from "next/image";

export const getStaticProps = async ({
  locale = "en",
}: GetStaticPropsContext) => {
  const programsPage = await getProgramsPage({ locale });
  return { props: programsPage };
};

export default function ProgramsPage({
  mainTitle,
  banner,
  coloredPrimaryTitle,
  coloredSecondaryTitle,
  coloredSubtitle,
  firstParagraph,
  firstArticleImage,
  firstSectionListTitle,
  firstSectionList,
  secondSectionTitle,
  secondSectionSubtitle,
  secondSectionImage,
  secondSectionParagraph,
  secondSectionSecondTitle,
  secondSectionFirstList,
  secondSectionThirdTitle,
  secondSectionSecondList,
  quote,
  thirdSectionBoldTitle,
  thirdSectionTitle,
  thirdSectionText,
  thirdSectionButtonText,
}: TypeProgramsPageFields) {
  return (
    <Layout>
      <Header
        className="bg-[url('/images/persons/stephanie.png')]"
        title={mainTitle}
      />
      <main>
        <Banner
          title={banner.fields.title}
          text={banner.fields.text}
          color="mauve"
        />
        <Section className="pb-[72px] pt-24">
          <GridLayout className="mb-20">
            <article>
              <BicoloredTitle
                subtitle={coloredSubtitle}
                primary={coloredPrimaryTitle}
                secondary={coloredSecondaryTitle}
                primaryColor="mauve"
              />
              <p className="text-r-3xl mt-5 font-semibold text-neutral-600">
                {firstParagraph}
              </p>
            </article>
            <Image
              className="justify-self-center rounded-lg"
              src={getContentfulImageUrl(
                firstArticleImage.fields.image.fields.file.url,
                {
                  width: 520,
                  height: 393,
                  fit: "fill",
                }
              )}
              alt={firstArticleImage.fields.alt}
              width={520}
              height={393}
            />
          </GridLayout>
          <article className="mt-10 rounded-lg bg-gray p-10 pb-20">
            <h4 className="text-r-4xl font-semibold text-green-500">
              {firstSectionListTitle}
            </h4>
            <ul className="text-r-2xl mt-10 grid list-disc grid-cols-1 gap-x-10 gap-y-5 px-10 font-bold md:grid-cols-2">
              {firstSectionList.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </Section>
        <div className="bg-pink-salt">
          <Section className="pt-20">
            <GridLayout className="items-center">
              <Title
                subtitle={secondSectionSubtitle}
                title={secondSectionTitle}
              />
              <p className="text-r-2xl text-neutral-700">
                {secondSectionParagraph}
              </p>
            </GridLayout>
            <ScrollableImage
              className="my-20"
              src={getContentfulImageUrl(
                secondSectionImage.fields.image.fields.file.url
              )}
              width={1266}
              height={185}
              alt={secondSectionImage.fields.alt}
            />
            <GridLayout gap={24} className="mb-16 items-start">
              <article>
                <h4 className="text-r-3xl font-semibold text-gray-700">
                  {secondSectionSecondTitle}
                </h4>
                <ul className="text-r-2xl mt-10 grid grid-cols-1 max-w-[300px] mx-auto sm:max-w-none sm:grid-cols-2 gap-10 font-bold">
                  {secondSectionFirstList.map((item) => (
                    <li
                      className="flex h-32 text-base uppercase items-center rounded-lg bg-white p-6"
                      key={item}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="ml-2 mt-8 flex">
                  {/* <ArrowedButtonLink
                  href={"#"}
                  text={"Télécharger le catalogue Afi U."}
                /> */}
                </div>
              </article>
              <article>
                <h4 className="text-r-3xl font-semibold text-gray-700">
                  {secondSectionThirdTitle}
                </h4>
                <ul className="text-r-2xl w-2/3 list-disc space-y-10 pl-10 pt-10">
                  {secondSectionSecondList.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            </GridLayout>
            <GridLayout className="py-24">
              <Image
                className="mx-auto rounded-full bg-green-300"
                src={getContentfulImageUrl(
                  quote.fields.image.fields.image.fields.file.url
                )}
                alt={quote.fields.image.fields.alt}
                width={340}
                height={340}
              />
              <article>
                <p className="text-r-4xl mb-10 font-semibold text-green-500">
                  {quote.fields.text}
                </p>
                <label className="text-r-2xl">
                  <span className="text-r-3xl font-semibold">
                    {quote.fields.fullName}
                  </span>
                  <br />
                  {quote.fields.role}
                </label>
              </article>
            </GridLayout>
          </Section>
        </div>
        <div className="bg-[url('/images/womens-discussing.png')] bg-cover bg-[70%] md3:bg-right bg-no-repeat">
          <Section className="mb-12 py-[72px] md:mb-10">
            <GridLayout breakpoint="lg">
              <div className="flex flex-col items-start mb-6 mt-[500px] rounded-lg bg-white px-5 py-8 sm:mt-[600px] lg:mb-0 lg:mt-0 lg:py-20">
                <h3 className="text-r-4xl">
                  <span className="font-semibold">{thirdSectionBoldTitle}</span>
                  <br />
                  {thirdSectionTitle}
                </h3>
                <p className="text-r-2xl py-8">{thirdSectionText}</p>
                <ArrowedButtonLink
                  rel="noreferrer"
                  target="_blank"
                  href={
                    "https://lp.afiexpertise.com/contactez-nous-afi-par-edgenda"
                  }
                  text={thirdSectionButtonText}
                />
              </div>
            </GridLayout>
          </Section>
        </div>
      </main>
    </Layout>
  );
}
